import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { Check, Close, OpenInNew } from '@mui/icons-material';

/**
 * Widget Preview Component
 * Renders a preview of the comparison table as it would appear in production
 * Based on the script_impresoras_kalunga.11.js structure
 */
const WidgetPreview = ({ products, specsToShow, specsLabels = {}, title = "Leve sua experiência para o próximo nível" }) => {
  // Get spec label
  const getSpecLabel = (key) => {
    if (specsLabels[key]) return specsLabels[key];
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format spec value
  const formatSpecValue = (value, key) => {
    if (value === null || value === undefined || value === '') {
      return <Typography sx={{ color: '#999' }}>—</Typography>;
    }
    
    // Boolean values - show checkbox
    if (typeof value === 'boolean' || key === 'duplex_automatico' || key === 'adf') {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            border: '2px solid #000',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: value ? '#000' : '#fff',
            color: value ? '#fff' : 'transparent',
            mx: 'auto',
          }}
        >
          {value && <Check sx={{ fontSize: 16 }} />}
        </Box>
      );
    }
    
    // Object values (like velocidad_impresion)
    if (typeof value === 'object' && value !== null) {
      return (
        <Box sx={{ fontSize: 14 }}>
          {Object.entries(value).map(([k, v]) => (
            <Box key={k}>
              {k.charAt(0).toUpperCase() + k.slice(1)}: {v}
            </Box>
          ))}
        </Box>
      );
    }
    
    // String values
    return <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line' }}>{String(value)}</Typography>;
  };

  // Format price
  const formatPrice = (price) => {
    if (!price || price === 'NOT_FOUND') return '—';
    const numericPrice = parseFloat(String(price).replace(/[^\d.,]/g, '').replace(',', '.'));
    if (isNaN(numericPrice)) return price;
    return `R$ ${numericPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (!products || products.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography>No hay productos para previsualizar</Typography>
      </Box>
    );
  }

  const orderedSpecs = specsToShow && specsToShow.length > 0 
    ? specsToShow 
    : Object.keys(products[0]?.specs || {}).filter(k => !['sku', 'modelo'].includes(k));

  return (
    <Box
      sx={{
        fontFamily: '"Inter", "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: '#fff',
        p: 3,
        borderRadius: 2,
        border: '1px solid #e0e0e0',
      }}
    >
      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: '#000',
          textAlign: 'center',
          mb: 4,
          fontSize: { xs: 20, md: 28 },
        }}
      >
        {title}
      </Typography>

      {/* Comparison Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: products.length * 200 + 180,
            borderCollapse: 'collapse',
            fontSize: 14,
            textAlign: 'left',
            backgroundColor: '#fff',
            borderBottom: '2px solid #000',
            '& td, & th': {
              p: '12px 16px',
              verticalAlign: 'middle',
              backgroundColor: '#fff',
              textAlign: 'center',
              borderBottom: '1px solid #000',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
            },
            '& th:not(.spec-label), & td:not(.spec-label)': {
              width: 200,
              minWidth: 200,
              maxWidth: 220,
            },
            '& .spec-label': {
              textAlign: 'left',
              fontWeight: 600,
              color: '#000',
              width: 180,
              minWidth: 180,
              fontSize: 16,
            },
            '& .current-product': {
              backgroundColor: '#fff',
              borderLeft: '3px solid #000',
              borderRight: '3px solid #000',
            },
            '& thead .current-product': {
              borderTop: '3px solid #000',
            },
            '& tbody tr:last-child .current-product': {
              borderBottom: '3px solid #000',
            },
          }}
        >
          {/* Header */}
          <thead>
            <tr>
              <th className="spec-label"></th>
              {products.map((product, index) => (
                <th key={product.sku} className={index === 0 ? 'current-product' : ''}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 280, py: 2 }}>
                    {/* Product Image */}
                    <Box sx={{ height: 80, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.title}
                        sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>

                    {/* Product Info */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 90 }}>
                      <Typography variant="caption" sx={{ color: '#000', mb: 0.5 }}>
                        {product.category}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 16, lineHeight: 1.2, mb: 0.5 }}>
                        {product.title}
                      </Typography>
                      {index === 0 ? (
                        <Typography variant="caption" sx={{ color: '#000' }}>(Este produto)</Typography>
                      ) : (
                        <Box
                          component="a"
                          href={product.link}
                          target="_blank"
                          rel="noopener"
                          sx={{ color: '#3483FA', textDecoration: 'none', fontSize: 12, '&:hover': { textDecoration: 'underline' } }}
                        >
                          Ver produto
                        </Box>
                      )}
                    </Box>

                    {/* Buy Button */}
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      sx={{
                        mt: 'auto',
                        backgroundColor: '#000',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: 14,
                        py: 1,
                        '&:hover': { backgroundColor: '#333' },
                      }}
                    >
                      Comprar
                    </Button>
                  </Box>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {/* Price Row */}
            <tr>
              <td className="spec-label">Preco</td>
              {products.map((product, index) => (
                <td key={`price-${product.sku}`} className={index === 0 ? 'current-product' : ''}>
                  <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
                    {formatPrice(product.prices || product.price)}
                  </Typography>
                </td>
              ))}
            </tr>

            {/* Spec Rows */}
            {orderedSpecs.map((specKey) => (
              <tr key={specKey}>
                <td className="spec-label">{getSpecLabel(specKey)}</td>
                {products.map((product, index) => (
                  <td key={`${specKey}-${product.sku}`} className={index === 0 ? 'current-product' : ''}>
                    {formatSpecValue(product.specs?.[specKey], specKey)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
        <Chip label={`${products.length} productos`} size="small" />
        <Chip label={`${orderedSpecs.length} specs`} size="small" variant="outlined" />
      </Box>
    </Box>
  );
};

export default WidgetPreview;
